import { LinkExternalIcon } from '@orn/shared-web/icons';
import hostnameLabel from '@orn/shared-web/utils/url/hostnameLabel';

import { formatAccountSubtype } from '../../../utils/account/accounts';
import { formatInvestmentTaxTreatment, investmentTaxTreatment } from '../../../utils/account/investmentTaxTreatment';
import Widget from '../../Widget';

import type { FinancialAccountSubtype } from '../../../__generated__/globalTypes';

export interface IInvestmentAccountSummaryProps {
  subtype?: FinancialAccountSubtype | null;
  bankName: string;
  /** The institution's website, already limited to http(s) by the server. */
  bankUrl?: string | null;
  className?: string;
}

/**
 * "Summary" card for an investment account: the account type, how it is taxed, and a link out to
 * the institution. Each row is left out when its data is missing. A label and its value share a
 * line when they fit; otherwise the value goes on a line of its own.
 */
const InvestmentAccountSummary = ({ subtype, bankName, bankUrl, className }: IInvestmentAccountSummaryProps) => {
  const treatment = investmentTaxTreatment(subtype);
  const hostname = bankUrl ? hostnameLabel(bankUrl) : null;

  return (
    <Widget className={className}>
      <Widget.Header title="Summary" />

      <Widget.Body className="pb-2 pt-1">
        {subtype && <Widget.Row variant="summary" label="Account type" value={formatAccountSubtype(subtype)} />}
        {treatment && (
          <Widget.Row variant="summary" label="Tax treatment" value={formatInvestmentTaxTreatment(treatment)} />
        )}
        {bankUrl && hostname && (
          <Widget.Row
            variant="summary"
            label={`Open at ${bankName}`}
            value={
              <a
                href={bankUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-secondary hover:text-brand-secondary_hover flex items-center gap-1.5"
              >
                <span className="truncate">{hostname}</span>
                <LinkExternalIcon className="size-4 shrink-0" />
              </a>
            }
          />
        )}
      </Widget.Body>
    </Widget>
  );
};

export default InvestmentAccountSummary;
